import { useLayoutEffect, useMemo } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import {
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { openModal } from '@mantine/modals';
import { IconAlertCircle } from '@tabler/icons-react';
import { mapUserRole } from '@utils/mappers';

import { User } from '@interfaces/users.interface';

import { useGetCustomerByIdQuery } from '@api/customer.api';
import {
  useActivateUserMutation,
  useDeactivateUserMutation,
  useGetUserByIdQuery,
} from '@api/users.api';

import useAuth from '@hooks/useAuth';

import ApproveCustomerModal from '@components/ApproveCustomerModal';
import { LayoutContextType } from '@components/layout/Layout';

import NotFound from './NotFound';

export default function UserDetail() {
  // ==========================================================================
  // General
  // ==========================================================================
  const { id } = useParams();
  const { user: currentUser } = useAuth();

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data: userData, isLoading, error } = useGetUserByIdQuery(+id!);
  const { data: customerData, isLoading: isLoadingCustomer } =
    useGetCustomerByIdQuery(+id!, {
      skip: !userData || userData.role !== 'customer',
    });

  const [activateUser] = useActivateUserMutation();
  const [deactivateUser] = useDeactivateUserMutation();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleApproveCustomer = (user: User) => {
    openModal({
      title: 'Approva cliente',
      children: <ApproveCustomerModal user={user} />,
    });
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  const { setLayoutProps } = useOutletContext<LayoutContextType>();

  const backLink = useMemo(
    () => ({
      title: userData?.role === 'admin' ? 'Amministratori' : 'Clienti',
      to: userData?.role === 'admin' ? '/amministratori' : '/clienti',
    }),
    [userData?.role],
  );

  useLayoutEffect(() => {
    setLayoutProps({
      title: `${userData?.firstName || ''} ${userData?.lastName || ''}`,
      backLink,
    });
  }, [setLayoutProps, userData, backLink]);

  return isLoading || isLoadingCustomer ? (
    <Loader />
  ) : error || !userData ? (
    <NotFound />
  ) : (
    <Group align="start" gap="xl">
      <Paper withBorder p="lg">
        <Title order={3} mb="sm">
          Informazioni utente
        </Title>
        <Group gap="xs">
          <Text fw="bold">Nome:</Text>
          <Text>{userData.firstName}</Text>
        </Group>
        <Group gap="xs">
          <Text fw="bold">Cognome:</Text>
          <Text>{userData.lastName}</Text>
        </Group>
        <Group gap="xs">
          <Text fw="bold">Email:</Text>
          <Text>{userData.email}</Text>
        </Group>
        <Group gap="xs">
          <Text fw="bold">Ruolo:</Text>
          <Text>{mapUserRole(userData.role)}</Text>
        </Group>
        <Group gap="xs">
          <Text fw="bold">Stato:</Text>
          {userData.adminApprovedAt ? (
            <Tooltip
              label={
                userData.id !== currentUser!.id
                  ? userData.active
                    ? 'Disattiva utente'
                    : 'Attiva utente'
                  : 'Non puoi modificare il tuo stato'
              }
            >
              <Badge
                color={userData.active ? 'green' : 'red'}
                style={{
                  cursor:
                    userData.id !== currentUser!.id ? 'pointer' : undefined,
                }}
                onClick={
                  userData.id !== currentUser!.id && userData.active
                    ? () => deactivateUser(userData.id)
                    : () => activateUser(userData.id)
                }
              >
                {userData.active ? 'Attivo' : 'Inattivo'}
              </Badge>
            </Tooltip>
          ) : (
            <>
              <Tooltip label="Utente in attesa di approvazione">
                <IconAlertCircle />
              </Tooltip>
              <Button onClick={() => handleApproveCustomer(userData)} size="xs">
                Approva
              </Button>
            </>
          )}
        </Group>
      </Paper>
      {customerData && (
        <>
          <Paper withBorder p="lg">
            <Title order={3} mb="sm">
              Informazioni cliente
            </Title>
            <Group gap="xl" align="start">
              <div>
                <Group gap="xs">
                  <Text fw="bold">P.IVA:</Text>
                  <Text>{customerData.vatNumber}</Text>
                </Group>
                <Group gap="xs">
                  <Text fw="bold">Azienda:</Text>
                  <Text>{customerData.company}</Text>
                </Group>
                <Group gap="xs">
                  <Text fw="bold">Reparto:</Text>
                  <Text>{customerData.department || '-'}</Text>
                </Group>
                <Group gap="xs">
                  <Text fw="bold">Laboratorio:</Text>
                  <Text>{customerData?.laboratory || '-'}</Text>
                </Group>
                <Group gap="xs">
                  <Text fw="bold">Mercati di interesse:</Text>
                  <Text>
                    {customerData.allowedCategories.join(', ') || '-'}
                  </Text>
                </Group>
                <Group gap="xs">
                  <Text fw="bold">Settori di interesse:</Text>
                  <Text>{customerData.interestSector.join(', ') || '-'}</Text>
                </Group>
                <Group gap="xs">
                  <Text fw="bold">Tecniche utilizzate:</Text>
                  <Text>{customerData.techniquesUsed.join(', ') || '-'}</Text>
                </Group>
                <Group gap="xs">
                  <Text fw="bold">Goodfellow:</Text>
                  <Text>{customerData.goodfellow.join(', ') || '-'}</Text>
                </Group>
                <Group gap="xs">
                  <Text fw="bold">Note aggiuntive:</Text>
                  <Text>{customerData.additionalNotes || '-'}</Text>
                </Group>
              </div>
              <div>
                <Group gap="xs">
                  <Text fw="bold">Telefono:</Text>
                  <Text>{customerData.phone || '-'}</Text>
                </Group>
                <Group gap="xs">
                  <Text fw="bold">Sito web:</Text>
                  <Text>{customerData.website || '-'}</Text>
                </Group>
                <Group gap="xs">
                  <Text fw="bold">Nazione:</Text>
                  <Text>{customerData.country}</Text>
                </Group>
                <Group gap="xs">
                  <Text fw="bold">Regione:</Text>
                  <Text>{customerData.region || '-'}</Text>
                </Group>
                <Group gap="xs">
                  <Text fw="bold">Provincia:</Text>
                  <Text>{customerData.province}</Text>
                </Group>
                <Group gap="xs">
                  <Text fw="bold">Città:</Text>
                  <Text>{customerData.city}</Text>
                </Group>
                <Group gap="xs">
                  <Text fw="bold">CAP:</Text>
                  <Text>{customerData.postalCode}</Text>
                </Group>
                <Group gap="xs">
                  <Text fw="bold">Indirizzo:</Text>
                  <Text>{customerData.address}</Text>
                </Group>
              </div>
            </Group>
          </Paper>
        </>
      )}
    </Group>
  );
}
